import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_grainette-migration/artifacts/i2pxliea__Grainette%20_%20.png";

const ORDER_LINKS = [
  { label: "Grainette Salades - Mogador", href: "https://clicks.tastycloud.fr/f8def329f79cc069cc86029fd12781cb49a501fadf230be5f094e9aeaf656e66/menu/73742" },
  { label: "Grainette Salades - Godot de Mauroy", href: "https://commandes.grainette.fr/home/1064/2/menu" },
  { label: "Grainette Pâtes - Godot de Mauroy", href: "https://commandes.grainette.fr/home/4404/2/menu" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOrderOpen(false);
  }, [location]);

  const navLinks = [
    { label: "Menu Salades", to: "/menus-salades" },
    { label: "Menu Pâtes", to: "/menus-pates" },
    { label: "Allergènes", to: "/allergenes" },
    { label: "Recrutement", to: "/recrutement" },
    { label: "Contact", to: "/contact" },
  ];

  const isActive = (to) => location.pathname === to;

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHome
          ? scrolled ? 'navbar-glass shadow-sm' : 'bg-transparent'
          : 'navbar-glass shadow-sm'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — ramène à l'accueil */}
          <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="Grainette — Retour à l'accueil"
              className="h-16 w-auto object-contain transition-all duration-300"
              style={{
                filter: isHome && !scrolled
                  ? 'brightness(0) invert(1)'      /* blanc sur homepage transparente */
                  : 'brightness(0) invert(0)',     /* noir sur navbar blanche */
              }}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '')}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-sage bg-sage/10'
                    : isHome && !scrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/10'
                    : 'text-gray-600 hover:text-sage hover:bg-sage/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Commander button */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setOrderOpen(!orderOpen)}
                data-testid="commander-btn"
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white rounded-full btn-lift"
                style={{ backgroundColor: '#80a890' }}
              >
                Commander
                <ChevronDown size={14} className={`transition-transform duration-200 ${orderOpen ? 'rotate-180' : ''}`} />
              </button>
              {orderOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  {ORDER_LINKS.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`order-link-${link.label.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                      className="block px-4 py-3 text-sm text-gray-700 hover:bg-sage/10 hover:text-sage transition-colors"
                      onClick={() => setOrderOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.replace('/', '')}`}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-sage/15 text-sage'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
              {ORDER_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 text-sm font-semibold text-white rounded-xl text-center"
                  style={{ backgroundColor: '#80a890' }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
